// lib/export-size-draft.ts
import type { DraftBound } from '#types/types';

type ImageSizeAxis = 'megapixels' | 'width' | 'height';

// A French keyboard types a decimal comma.
export const parseDraftNumber = (text: string): number | null => {
  const normalized = text.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};

export const clampWithBound = (value: number, min: number, max: number): { value: number; bound: DraftBound } => {
  if (value > max) return { value: max, bound: 'max' };
  if (value < min) return { value: min, bound: 'min' };
  return { value, bound: null };
};

export const sideLimitBound = (width: number, height: number, minSide: number, maxSide: number): DraftBound => {
  if (Math.max(width, height) > maxSide) return 'max';
  if (Math.min(width, height) < minSide) return 'min';
  return null;
};

// Bounds are applied by scaling both sides, so the aspect ratio survives the clamp.
export const resolveImageExportSize = (
  axis: ImageSizeAxis,
  value: number,
  aspectRatio: number,
  minSide: number,
  maxSide: number,
): { megapixels: number; width: number; height: number; bound: DraftBound } | null => {
  if (!(value > 0) || !(aspectRatio > 0)) return null;
  const height = axis === 'megapixels'
    ? Math.sqrt((value * 1_000_000) / aspectRatio)
    : (axis === 'height' ? value : value / aspectRatio);
  const width = axis === 'width' ? value : height * aspectRatio;
  const bound = sideLimitBound(width, height, minSide, maxSide);
  const scale = bound === 'max'
    ? maxSide / Math.max(width, height)
    : (bound === 'min' ? minSide / Math.min(width, height) : 1);
  const boundedWidth = width * scale;
  const boundedHeight = height * scale;
  return {
    megapixels: (boundedWidth * boundedHeight) / 1_000_000,
    width: boundedWidth,
    height: boundedHeight,
    bound,
  };
};

const BASE_COVERAGE_RATIO = 0.4;
const DEFAULT_MAX_PATCH_LONG_SIDE_PX = 7680;
const COVERAGE_GRID_CELLS = 64;

export type OptimalExportReason = 'base' | 'patch' | 'capped' | 'sparse';

type ExportRect = { x: number; y: number; w: number; h: number };

const intersects = (a: ExportRect, b: ExportRect): boolean => (
  Math.min(a.x + a.w, b.x + b.w) > Math.max(a.x, b.x) && Math.min(a.y + a.h, b.y + b.h) > Math.max(a.y, b.y)
);

// density = bitmap pixels per world unit. The base is the densest level that layers at least that
// dense cover 40 % of the export with (their union, on a coarse grid: stacked patches over one zone
// count once), so a face or a small inpaint upscale cannot set it; such a patch may only lift the
// result up to an 8K long side. Without a base, every layer is a small area: the densest of them,
// still capped at 8K.
export const resolveOptimalExportMegapixels = (
  layers: ReadonlyArray<{ density: number; rect: ExportRect }>,
  exportRect: ExportRect,
  maxPatchLongSidePx = DEFAULT_MAX_PATCH_LONG_SIDE_PX,
): { megapixels: number; reason: OptimalExportReason } | null => {
  const exportArea = exportRect.w * exportRect.h;
  if (!(exportArea > 0)) return null;
  const samples = layers
    .filter((layer) => Number.isFinite(layer.density) && layer.density > 0 && intersects(layer.rect, exportRect))
    .sort((left, right) => right.density - left.density);
  const densest = samples[0]?.density;
  if (densest === undefined) return null;

  const cellW = exportRect.w / COVERAGE_GRID_CELLS;
  const cellH = exportRect.h / COVERAGE_GRID_CELLS;
  const covered = new Uint8Array(COVERAGE_GRID_CELLS * COVERAGE_GRID_CELLS);
  let coveredCells = 0;
  let baseDensity: number | null = null;
  for (const sample of samples) {
    for (let row = 0; row < COVERAGE_GRID_CELLS; row += 1) {
      const centerY = exportRect.y + (row + 0.5) * cellH;
      if (centerY < sample.rect.y || centerY >= sample.rect.y + sample.rect.h) continue;
      for (let column = 0; column < COVERAGE_GRID_CELLS; column += 1) {
        const centerX = exportRect.x + (column + 0.5) * cellW;
        const index = row * COVERAGE_GRID_CELLS + column;
        if (covered[index] || centerX < sample.rect.x || centerX >= sample.rect.x + sample.rect.w) continue;
        covered[index] = 1;
        coveredCells += 1;
      }
    }
    if (coveredCells >= BASE_COVERAGE_RATIO * covered.length) {
      baseDensity = sample.density;
      break;
    }
  }

  const patchCapDensity = maxPatchLongSidePx / Math.max(exportRect.w, exportRect.h);
  const patchDensity = Math.min(densest, patchCapDensity);
  let density = patchDensity;
  let reason: OptimalExportReason = 'sparse';
  if (baseDensity !== null) {
    density = Math.max(baseDensity, patchDensity);
    reason = density === baseDensity ? 'base' : (densest > patchCapDensity ? 'capped' : 'patch');
  }
  return { megapixels: (exportArea * density * density) / 1_000_000, reason };
};
